import {AfterViewInit, Component, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {NgForm} from '@angular/forms';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {ReplaySubject} from 'rxjs';
import {UserPermissions} from '../../../../shared/interface/user-permissions';
import {MatDialog} from '@angular/material/dialog';
import {MatSnackBar} from '@angular/material/snack-bar';
import {AuthService} from '../../../../shared/services/auth.service';
import {FilterService} from '../../../../shared/services/filter.service';
import {ActivatedRoute, Router} from '@angular/router';
import {select, Store} from '@ngrx/store';
import {StoreRootState} from '../../../../shared/ngrx/reducers';
import {delay, distinctUntilChanged, takeUntil} from 'rxjs/operators';
import {CountedAndPaginated} from '../../../../shared/interface/response/counted-and-paginated';
import {getCurrentRouteState} from '../../../../shared/ngrx/selector';
import {RouterStateUrl} from '../../../../shared/ngrx/route-serializer';
import {Uuid} from '../../../../shared/interface/response/uuid';
import {JobResultDialogComponent} from '../../../../shared/components/job-result-dialog/job-result-dialog.component';
import {HttpErrorResponse} from '@angular/common/http';
import {CommissionImportRow} from '../../../../shared/interface/commission-import-row';
import {CommissionImportRowService} from '../../../../shared/services/commission-import-row.service';
import {InsuranceCompany} from '../../../../shared/interface/insurance-company';
import {Sort} from '@angular/material/sort';
import {CommissionSettlement} from "../../../../shared/interface/commission-settlement";

@Component({
  selector: 'app-all-commissions-list-invoices',
  templateUrl: './all-commissions-list-invoices.component.html',
  styleUrls: ['./all-commissions-list-invoices.component.scss']
})
export class AllCommissionsListInvoicesComponent implements AfterViewInit, OnInit, OnDestroy {
  @Input() commissionRedirection: string = null;
  @Input() commissionSettlement?: Array<any>;
  @Input() contract: string = null;
  @Input() embeddedView = false;
  @Input() enableFilters?: string[] = null;
  @ViewChild('filtersForm') filtersForm: NgForm;
  @ViewChild(MatPaginator) paginator: MatPaginator;

  initialFilters: any;
  rows: CommissionImportRow[] = [];
  displayedColumns: string[] = [
    'billingMonth', 'insuranceCompany', 'contract', 'ofwca', 'policyNumber', 'collection', 'rate', 'commission',
    'internalRisk', 'partner', 'partnerAssetsRegional', 'status',
  ];
  totalData = 0;
  userPermissions: ReplaySubject<UserPermissions>;
  selectedInsuranceCompanies: InsuranceCompany[] = [];
  sortBy = 'createdAt';
  sortOrder = 'desc';
  loadingData = true;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private commissionImportRowService: CommissionImportRowService,
    private dialog: MatDialog,
    private snack: MatSnackBar,
    private auth: AuthService,
    private filterService: FilterService,
    private activatedRoute: ActivatedRoute,
    private store: Store<StoreRootState>,
    private router: Router,
  ) {
    this.userPermissions = auth.getUserPermissions();
    this.commissionImportRowService.rowListSubject
      .pipe(
        takeUntil(this.destroyed$),
        delay(0)
      ).subscribe({
      next: (response: CountedAndPaginated<CommissionImportRow>) => {
        this.loadingData = false;
        this.rows = response.items;
        this.totalData = response.pagination.total;
      },
      error: (response: HttpErrorResponse) => {
        this.snack.open('Przy ładowaniu danych wystąpił błąd', response.status.toString(), {duration: 2000});
        this.loadingData = false;
      }
    });

    this.parseFilters();
  }

  parseFilters(filters?: any): void {
    this.initialFilters = this.filterService.parseFiltersFromQueryParams({
      in$insuranceCompany: 'int[]',
      containsFromLeft$policyNumber: 'string',
      company: 'int',
      ofwca: 'string',
      partner: 'string',
      gte$collection: 'string',
      lte$collection: 'string',
      partnerAssetsRegional: 'string',
      ofwcaAssetsRegional: 'string',
      notMatched: 'string[]',
      matched: 'string[]',
      contract: 'string',
      importer: 'int[]',
      gte$createdAt: 'string',
      lte$createdAt: 'string',
      risk: 'int',
      in$status: 'int[]',
      settlementIn: 'string[]',
      billingMonth: 'date',
    }, filters);
  }

  ngOnInit(): void {

  }

  ngAfterViewInit(): void {
    this.paginator.page
      .pipe(
        takeUntil(this.destroyed$)
      ).subscribe((event: PageEvent) => {
      this.loadingData = true;
      if (this.embeddedView) {
        this.loadListData();
      } else {
        this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
          page: event.pageIndex,
          pageSize: event.pageSize,
          sortBy: this.sortBy,
          sortOrder: this.sortOrder,
          ...this.filtersForm.value
        })]);
      }
    });

    if (!this.embeddedView) {
      this.store
        .pipe(
          select(getCurrentRouteState),
          delay(0),
          distinctUntilChanged((prev: RouterStateUrl, curr: RouterStateUrl) => prev.url === curr.url),
          takeUntil(this.destroyed$),
        )
        .subscribe((state: RouterStateUrl) => {
          this.parseFilters(state.params);
          this.paginator.pageIndex = state.params.page;
          this.paginator.pageSize = state.params.pageSize;
          this.sortBy = state.params?.sortBy ? state.params.sortBy : 'createdAt';
          this.sortOrder = state.params?.sortOrder ? state.params.sortOrder : 'desc';
          this.loadListData();
        })
      ;
    } else {
      this.loadListData();
    }
  }

  sortChanged($event: Sort): void {
    this.sortBy = $event.active;
    this.sortOrder = $event.direction;

    if (!this.embeddedView) {
      this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
        sortBy: $event.active,
        sortOrder: $event.direction,
        page: this.paginator.pageIndex,
        pageSize: this.paginator.pageSize,
        ...this.filtersForm.value
      })]);
    } else {
      this.loadListData();
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  buildFilters(): any {
    const filters = {...this.initialFilters};
    const uuid = [];
    if (null !== this.commissionRedirection) {
      filters.commissionRedirection = this.commissionRedirection;
    }

    if (null !== this.commissionSettlement) {
      this.commissionSettlement.forEach((res: CommissionSettlement) => {
        uuid.push(res.uuid);
      });
      filters.settlementIn = uuid.toString();
    }

    if (null !== this.contract) {
      filters.contract = this.contract;
    }

    return filters;
  }

  loadListData(): void {
    this.loadingData = true;
    this.commissionImportRowService.loadList(
      this.paginator.pageIndex,
      this.paginator.pageSize,
      this.buildFilters(),
      {
        sortBy: this.sortBy,
        sortOrder: this.sortOrder
      });
  }

  filter(): void {
    if (this.loadingData) {
      return;
    }

    if (this.embeddedView) {
      if (this.paginator.pageIndex === 0) {
        this.loadListData();
      } else {
        this.paginator.firstPage();
      }
    } else {
      this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
        page: 0,
        pageSize: this.paginator.pageSize,
        sortBy: this.sortBy,
        sortOrder: this.sortOrder,
        ...this.filtersForm.value,
      })]);
    }
  }

  downloadReport(): void {
    this.commissionImportRowService.downloadReport(this.buildFilters())
      .subscribe((response: Uuid) => {
        this.dialog.open(JobResultDialogComponent, {
          disableClose: true,
          width: '500px',
          data: {jobId: response.uuid, showProgress: false},
        });
      }, (error: HttpErrorResponse) => {
        this.snack.open('Przy generowaniu raportu wystąpił błąd', error.status.toString(), {duration: 2000});
      });
  }

  resetFilters(): void {
    this.filtersForm.resetForm();
    if (this.embeddedView) {
      this.loadListData();
    } else {
      this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, {
        page: 0,
        pageSize: this.paginator.pageSize,
        sortBy: this.sortBy,
        sortOrder: this.sortOrder,
      }]);
    }
  }

}
